/**
 * Where a reference's `description` came from, and what that means for AI
 * rewrites.
 *
 * `descriptionSource` is the provenance flag: `'ai'` (written by an AI rewrite),
 * `'imported'` (scraped or submitted copy), `'manual'` (a person wrote or edited
 * it). Absent means legacy — written before anything recorded the source.
 *
 * THE FLAG THAT PROTECTS ANYTHING IS `'manual'`. The review dialog pre-ticks a
 * regenerated description over every other source, legacy included (James's
 * call, 2026-09-21: an untagged description is treated as machine-made, and the
 * `originalDescription` snapshot keeps the first replacement recoverable). So a
 * hand edit that fails to record `'manual'` is a hand edit the next enrichment
 * may replace.
 */

export type DescriptionSource = 'ai' | 'imported' | 'manual';

/**
 * The provenance fields to save alongside a description, or nothing when the
 * description did not change.
 *
 * Keyed on the TEXT changing, not on the editor being touched: typing into the
 * box and back leaves the stored text — and its stored source — as they were.
 * `sessionSource` is the last thing that set the box this session: the AI apply
 * (`'ai'`) or the curator typing (`'manual'`), whichever came last.
 */
export function descriptionProvenancePatch<T>(
  storedDescription: string | undefined,
  savedDescription: string,
  sessionSource: DescriptionSource | undefined,
  now: T,
): { descriptionSource?: DescriptionSource; descriptionAiRewrittenAt?: T } {
  if ((storedDescription ?? '').trim() === savedDescription.trim()) return {};
  if (!sessionSource) return {};
  return sessionSource === 'ai'
    ? { descriptionSource: 'ai', descriptionAiRewrittenAt: now }
    : { descriptionSource: sessionSource };
}

/**
 * Should a regenerated description that DIFFERS from the stored one pre-tick?
 *
 * A description is prose written fresh on every run, so it never matches the
 * stored text and "differs" says nothing about disagreement — the question is
 * only whose text it would replace. Never a person's; anything else, yes.
 */
export function descriptionRewritePreTicks(storedSource: DescriptionSource | undefined): boolean {
  return storedSource !== 'manual';
}

/**
 * The source the pre-tick rule should judge by.
 *
 * A brand that turned on "Use my own description" in the portal
 * (`useAuthoredDescription`) wrote the stored text itself, whatever the flag
 * says. Brand edits only started recording `'manual'` with this change, so this
 * is what protects brand copy written before it — without a backfill.
 */
export function effectiveDescriptionSource(
  source: DescriptionSource | undefined,
  useAuthoredDescription: boolean | undefined,
): DescriptionSource | undefined {
  return useAuthoredDescription ? 'manual' : source;
}

/**
 * `{ descriptionSource: 'imported' }` when an import write CARRIES a description,
 * else nothing — spread into each of the importer's write payloads.
 *
 * Judged on the payload actually written, never on the mapped reference: the
 * merge path fills empty fields only, so a re-import that leaves a stored
 * description alone must not relabel whoever wrote it.
 */
export function importedDescriptionTag(
  written: Readonly<Record<string, unknown>>,
): { descriptionSource?: 'imported' } {
  const d = written.description;
  return typeof d === 'string' && d.trim() !== '' ? { descriptionSource: 'imported' } : {};
}

/**
 * What an import write should say about its description, given what the
 * preview's AI enrichment did to the row.
 *
 * `aiDescription` is the AI text the curator accepted on the preview; the write
 * is tagged `'ai'` (with the rewrite time and, fill-empty, the pre-AI text as
 * `originalDescription`) only when that is still exactly the description being
 * written — an edit after the AI apply falls back to `'imported'`, like any
 * other scraped copy. Without a hint this is {@link importedDescriptionTag}.
 */
export function importDescriptionTag<T>(
  written: Readonly<Record<string, unknown>>,
  hint: { _aiDescription?: string; _preAiDescription?: string } | undefined,
  existingOriginalDescription: string | undefined,
  now: T,
): { descriptionSource?: DescriptionSource; descriptionAiRewrittenAt?: T; originalDescription?: string } {
  const d = written.description;
  const ai = hint?._aiDescription?.trim();
  if (typeof d === 'string' && ai && d.trim() === ai) {
    const pre = hint?._preAiDescription?.trim();
    return {
      descriptionSource: 'ai',
      descriptionAiRewrittenAt: now,
      ...(pre && pre !== ai && !existingOriginalDescription?.trim() ? { originalDescription: pre } : {}),
    };
  }
  return importedDescriptionTag(written);
}
